import useFetch from "../../hooks/useFetch";
import { usePagination } from "../../hooks/usePagination";
import { useMemo, useState } from "react";
import {
  getSubscriptions,
  toggleSubscription,
  deleteSubscription,
  adminCancelSubscription,
  adminCreateSubscription,
} from "../../services/subscription";
import { files } from "../../services/files";
import { Form, message, TableColumnProps, Tag, Button, Dropdown, MenuProps, Modal } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import {
  INTERVALS_TEXT,
  PAYMENT_STATUS,
  PAYMENT_STATUS_COLOR,
} from "../../constants/constants";
import EyeButton from "../../components/general/eyeButton/eyeButton";
import useModal from "../../hooks/useModal";
import { IPayment, ISubscription } from "../../types/interfaces";
import { approvePayment, rejectPayment } from "../../services/payment";
import useAuth from "../../hooks/useAuth";
import Permission from "../../components/general/permission/permission";

function useManageSubscriptions() {
  const { onTableChange, tableParams } = usePagination();
  const { rolePermissions } = useAuth();
  
  const reviewModal = useModal();
  const assignModal = useModal();
  const paymentsModal = useModal();

  const memoizedPagination = useMemo(
    () => ({
      current: tableParams.pagination.current,
      pageSize: tableParams.pagination.pageSize,
      sorter: tableParams.sorter,
    }),
    [
      tableParams.pagination.current,
      tableParams.pagination.pageSize,
      tableParams.sorter,
    ],
  );

  const { data, error, loading, total, refetch } = useFetch({
    service: getSubscriptions,
    pagination: memoizedPagination,
  });

  const [form] = Form.useForm();
  const [submittingAssign, setSubmittingAssign] = useState(false);

  const [currentPayment, setCurrentPayment] = useState<IPayment | null>(null);
  const [currentUsername, setCurrentUsername] = useState<string>("");
  
  // State for payments modal
  const [paymentsSubscriptionId, setPaymentsSubscriptionId] = useState<number | null>(null);
  const [paymentsSubscriptionName, setPaymentsSubscriptionName] = useState<string>("");

  const handleApprove = async (id: number) => {
    try {
      reviewModal.loading.start();
      await approvePayment(id);
      handleCancel();
      refetch();
      message.success("Pago aprobado");
    } catch (error) {
      message.error(
        "Ha ocurrido un error inesperado, por favor intenta de nuevo",
      );
    } finally {
      reviewModal.loading.stop();
    }
  };

  const handleReject = async (id: number) => {
    try {
      reviewModal.loading.start();
      await rejectPayment(id);
      message.success("Pago rechazado");
      handleCancel();
      refetch();
    } catch (error) {
      message.error(
        "Ha ocurrido un error inesperado, por favor intenta de nuevo",
      );
    } finally {
      reviewModal.loading.stop();
    }
  };

  const handleCancel = () => {
    reviewModal.loading.stop();
    reviewModal.closeModal();
    setCurrentPayment(null);
    setCurrentUsername("");
  };

  const handleView = (record: ISubscription) => {
    reviewModal.openModal();
    setCurrentPayment(record.lastPayment);
    setCurrentUsername(record.userName);
  };

  const handleToggle = async (id: number) => {
    try {
      await toggleSubscription(id);
      message.success("Estado de suscripción actualizado");
      refetch();
    } catch (e: any) {
      message.error(e?.response?.data || "Error al actualizar estado");
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteSubscription(id);
      message.success("Suscripción eliminada");
      refetch();
    } catch (e: any) {
      message.error(e?.response?.data || "No se pudo eliminar (debe estar inactiva primero)");
    }
  };

  const handleAdminCancel = async (id: number) => {
    try {
      await adminCancelSubscription(id);
      message.success("Suscripción cancelada");
      refetch();
    } catch (e: any) {
      message.error(e?.response?.data || "Error al cancelar suscripción");
    }
  };

  const handleAssign = async (values: any) => {
    try {
      setSubmittingAssign(true);
      
      let receiptId: string | undefined;
      
      // If there's a receipt file, upload it first
      if (values.receiptFile) {
        try {
          const uploadResult = await files({
            file: values.receiptFile,
            category: "receipts",
          });
          if (uploadResult && (uploadResult as any).path) {
            receiptId = (uploadResult as any).path;
          }
        } catch (uploadError) {
          console.error("Error uploading receipt:", uploadError);
          message.warning("No se pudo subir el comprobante, pero se continuará con la asignación");
        }
      }
      
      const { receiptFile, ...submitData } = values;
      await adminCreateSubscription({
        ...submitData,
        receiptId,
      });
      message.success("Plan asignado correctamente");
      assignModal.closeModal();
      refetch();
    } catch (e: any) {
      console.error("Error al asignar plan:", e?.response?.data || e);
      const errDetail = typeof e?.response?.data === 'string' ? e.response.data : (e?.response?.data?.error || "Error al asignar plan");
      message.error(errDetail);
    } finally {
      setSubmittingAssign(false);
    }
  };

  const handleViewPayments = (record: any) => {
    setPaymentsSubscriptionId(record.id);
    setPaymentsSubscriptionName(`${record.userName} — ${record.name}`);
    paymentsModal.openModal();
  };

  const handleClosePayments = () => {
    paymentsModal.closeModal();
    setPaymentsSubscriptionId(null);
    setPaymentsSubscriptionName("");
  };

  const tableColumns: TableColumnProps[] = [
    {
      title: "Empresa",
      dataIndex: "userName",
      key: "userName",
      sorter: true,
    },
    {
      title: "Uso de licencias",
      key: "licenses",
      render: (_: string, record: any) => {
        return (
          <div style={{ fontWeight: "bold" }}>
             {record.activeEmployees} / {record.userLimit || "∞"}
          </div>
        );
      },
    },
    {
      title: "Recurrencia",
      dataIndex: "frequency",
      key: "frequency",
      render: (frequency: string) => {
        return INTERVALS_TEXT[frequency];
      },
    },
    {
      title: "Nombre del plan",
      dataIndex: "name",
      key: "name",
      sorter: true,
      render: (text: string) => {
         return <div style={{ fontWeight: "bold", color: "#3a6b0a" }}>{text}</div>;
      }
    },
    {
      title: "Fecha de inicio",
      dataIndex: "startDate",
      key: "startDate",
      sorter: true,
      render: (_: string, record: any) => {
        return <div>{record.startDate}</div>;
      },
    },
    {
      title: "Fecha fin",
      dataIndex: "endDate",
      key: "endDate",
      sorter: true,
      render: (_: string, record: any) => {
        return <div>{record.endDate || "—"}</div>;
      },
    },
    {
      title: "Estado de pago",
      dataIndex: ["lastPayment", "status"],
      key: "status",
      render: (status: string) => {
        return (
          <div>
            <Tag color={PAYMENT_STATUS_COLOR[status]}>
              {PAYMENT_STATUS[status] || "Desconocido"}
            </Tag>
          </div>
        );
      },
    },
    {
      title: "Estado",
      dataIndex: "active",
      key: "active",
      sorter: true,
      render: (active: boolean) => {
        return (
          <div>
            <Tag color={active ? "green" : "red"}>
              {active ? "Activo" : "Inactivo"}
            </Tag>
          </div>
        );
      },
    },
    {
      title: "Acciones",
      dataIndex: "actions",
      key: "actions",
      render: (_: string, record: any) => {
        const isAdmin = (rolePermissions || []).some((p) => 
          p.startsWith("ui:subscriptions:manage") || 
          p.startsWith("ui:subscriptions:toggle") || 
          p.startsWith("ui:subscriptions:delete")
        );

        if (!isAdmin) {
          return (
            <div className="actions" style={{ display: 'flex', gap: '8px' }}>
               {record.lastPayment?.receiptId && (
                 <EyeButton
                   record={record}
                   handleView={() => handleView(record)}
                 />
               )}
               <Button 
                 type="primary" 
                 size="small"
                 onClick={() => window.location.href = "/dashboard/subscriptions"}
                 style={{ borderRadius: "8px", background: "#81BC3D", borderColor: "#81BC3D" }}
               >
                 Actualizar Suscripción
               </Button>
            </div>
          );
        }

        const items: MenuProps['items'] = [];

        // View payments action
        if (rolePermissions?.includes("ui:subscriptions:manage")) {
          items.push({
            key: 'payments',
            label: 'Ver pagos',
            onClick: () => handleViewPayments(record),
          });
        }



        if (rolePermissions?.includes("ui:subscriptions:toggle") && !record.cancelled) {
          items.push({
            key: 'toggle',
            label: record.active ? 'Desactivar suscripción' : 'Activar suscripción',
            onClick: () => {
              Modal.confirm({
                title: '¿Confirmar cambio de estado?',
                content: record.active ? 'La empresa perderá acceso temporalmente.' : 'La empresa recuperará el acceso.',
                onOk: () => handleToggle(record.id),
              })
            },
          });
        }

        if (rolePermissions?.includes("ui:subscriptions:manage:all") && record.active) {
          items.push({
            key: 'cancel',
            danger: true,
            label: 'Cancelar suscripción',
            onClick: () => {
              Modal.confirm({
                title: '¿Cancelar suscripción?',
                content: 'Esto es irreversible y se establecerá la fecha de fin a hoy.',
                okText: 'Sí, cancelar',
                okType: 'danger',
                cancelText: 'No',
                onOk: () => handleAdminCancel(record.id),
              })
            },
          });
        }

        if (rolePermissions?.includes("ui:subscriptions:delete") && !record.active) {
          items.push({
            key: 'delete',
            danger: true,
            label: 'Eliminar registro (borrado real)',
            onClick: () => {
              Modal.confirm({
                title: '¿Eliminar definitivamente esta suscripción?',
                content: 'Se borrarán también los pagos vinculados. Esta acción es destructiva.',
                okText: 'Sí, eliminar',
                okType: 'danger',
                cancelText: 'No',
                onOk: () => handleDelete(record.id),
              })
            },
          });
        }

        return (
          <div className="actions" style={{ display: 'flex', gap: '8px' }}>
            <Dropdown menu={{ items }} trigger={['click']} disabled={items.length === 0}>
              <Button size="small" icon={<MoreOutlined />} style={{ borderRadius: "8px" }} />
            </Dropdown>
          </div>
        );
      },
    },
  ];

  return {
    currentPayment,
    currentUsername,
    data,
    error,
    form,
    handleApprove,
    handleCancel,
    handleReject,
    handleView,
    loading,
    onTableChange,
    refetch,
    reviewModal,
    assignModal,
    handleAssign,
    submittingAssign,
    tableColumns,
    tableParams,
    total,
    // Payments modal
    paymentsModal,
    paymentsSubscriptionId,
    paymentsSubscriptionName,
    handleViewPayments,
    handleClosePayments,
  };
}

export default useManageSubscriptions;
